import React, { memo, useId } from 'react';

/** Shared baseline where all building rects end (y + h). */
const SKYLINE_GROUND_Y = 190;

const BUILDINGS_BACK = [
    { x: 0, y: 118, w: 34, h: 72 },
    { x: 28, y: 102, w: 26, h: 88 },
    { x: 52, y: 112, w: 22, h: 78 },
    { x: 78, y: 96, w: 30, h: 94 },
    { x: 108, y: 108, w: 24, h: 82 },
    { x: 132, y: 90, w: 28, h: 100 },
    { x: 160, y: 104, w: 22, h: 86 },
    { x: 182, y: 114, w: 26, h: 76 },
    { x: 208, y: 98, w: 32, h: 92 },
    { x: 242, y: 110, w: 24, h: 80 },
    { x: 266, y: 92, w: 28, h: 98 },
    { x: 296, y: 106, w: 22, h: 84 },
    { x: 318, y: 100, w: 30, h: 90 },
    { x: 350, y: 112, w: 26, h: 78 },
    { x: 376, y: 104, w: 24, h: 86 },
];

const BUILDINGS_MID = [
    { x: 12, y: 128, w: 28, h: 62, windows: true },
    { x: 44, y: 120, w: 32, h: 70, stepped: true },
    { x: 82, y: 126, w: 24, h: 64, windows: true },
    { x: 112, y: 114, w: 36, h: 76, antenna: true },
    { x: 154, y: 122, w: 28, h: 68, windows: true },
    { x: 188, y: 116, w: 34, h: 74, stepped: true },
    { x: 228, y: 124, w: 26, h: 66 },
    { x: 258, y: 110, w: 38, h: 80, windows: true },
    { x: 302, y: 118, w: 30, h: 72, antenna: true },
    { x: 338, y: 126, w: 28, h: 64, windows: true },
    { x: 368, y: 120, w: 32, h: 70 },
];

const BUILDINGS_FRONT = [
    { x: 0, y: 138, w: 38, h: 52, windows: true },
    { x: 36, y: 132, w: 42, h: 58, stepped: true },
    { x: 82, y: 140, w: 30, h: 50 },
    { x: 118, y: 128, w: 44, h: 62, windows: true, antenna: true },
    { x: 168, y: 136, w: 36, h: 54, stepped: true },
    { x: 210, y: 130, w: 40, h: 60, windows: true },
    { x: 256, y: 134, w: 34, h: 56 },
    { x: 294, y: 126, w: 46, h: 64, windows: true },
    { x: 346, y: 138, w: 32, h: 52 },
    { x: 378, y: 132, w: 22, h: 58, stepped: true },
];

function Building({ x, y, w, h, fill, opacity, windows, stepped, antenna }) {
    const roofY = stepped ? y + 10 : y;

    return (
        <g opacity={opacity}>
            {stepped ? (
                <>
                    <rect x={x} y={y + 10} width={w} height={h - 10} fill={fill} />
                    <rect x={x + w * 0.15} y={y} width={w * 0.7} height={12} fill={fill} />
                </>
            ) : (
                <rect x={x} y={roofY} width={w} height={h - (roofY - y)} fill={fill} />
            )}
            {windows ? (
                <>
                    <rect
                        x={x + w * 0.22}
                        y={y + h * 0.28}
                        width={w * 0.22}
                        height={h * 0.14}
                        fill="#8ec9b8"
                        opacity="0.55"
                    />
                    <rect
                        x={x + w * 0.56}
                        y={y + h * 0.28}
                        width={w * 0.22}
                        height={h * 0.14}
                        fill="#8ec9b8"
                        opacity="0.55"
                    />
                    <rect
                        x={x + w * 0.22}
                        y={y + h * 0.52}
                        width={w * 0.22}
                        height={h * 0.14}
                        fill="#8ec9b8"
                        opacity="0.45"
                    />
                    <rect
                        x={x + w * 0.56}
                        y={y + h * 0.52}
                        width={w * 0.22}
                        height={h * 0.14}
                        fill="#8ec9b8"
                        opacity="0.45"
                    />
                </>
            ) : null}
            {antenna ? (
                <rect
                    x={x + w / 2 - 1}
                    y={y - 14}
                    width={2}
                    height={14}
                    fill={fill}
                    opacity="0.85"
                />
            ) : null}
        </g>
    );
}

function Cloud({ cx, cy, scale = 1, className }) {
    return (
        <g className={className}>
            <g transform={`translate(${cx} ${cy}) scale(${scale})`}>
                <ellipse cx="0" cy="4" rx="28" ry="11" fill="#fff" opacity="0.92" />
                <circle cx="-14" cy="0" r="12" fill="#fff" opacity="0.95" />
                <circle cx="6" cy="-6" r="14" fill="#fff" />
                <circle cx="20" cy="0" r="11" fill="#fff" opacity="0.95" />
            </g>
        </g>
    );
}

function Tree({ x, canopyY, scale = 1 }) {
    const trunkH = 14 * scale;
    const canopyH = 26 * scale;
    const canopyW = 18 * scale;

    return (
        <g transform={`translate(${x} 0) scale(${scale})`}>
            <rect
                x="-1.5"
                y={canopyY + canopyH - 4}
                width="3"
                height={trunkH}
                fill="#929081"
                rx="1"
            />
            <ellipse
                cx="0"
                cy={canopyY + canopyH * 0.42}
                rx={canopyW * 0.5}
                ry={canopyH * 0.5}
                fill="#3dac8a"
            />
        </g>
    );
}

export const GameBackground = memo(function GameBackground() {
    const uid = useId().replace(/:/g, '');
    const skyId = `lp-sky-${uid}`;
    const glowId = `lp-glow-${uid}`;

    return (
        <div className="lp-game__background" aria-hidden="true">
            <div className="lp-game__background-ambient">
                <span className="lp-game__background-glow lp-game__background-glow--left" />
                <span className="lp-game__background-glow lp-game__background-glow--right" />
                <span className="lp-game__background-shimmer" />
            </div>
            <svg
                className="lp-game__skyline"
                viewBox="0 0 400 240"
                preserveAspectRatio="xMidYMax slice"
                xmlns="http://www.w3.org/2000/svg"
                role="presentation"
            >
                <defs>
                    <linearGradient id={skyId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#dceefb" />
                        <stop offset="42%" stopColor="#ecf6fb" />
                        <stop offset="100%" stopColor="#f4fcf8" />
                    </linearGradient>
                    <radialGradient id={glowId} cx="50%" cy="88%" r="58%">
                        <stop offset="0%" stopColor="#b8e8d4" stopOpacity="0.45" />
                        <stop offset="100%" stopColor="#b8e8d4" stopOpacity="0" />
                    </radialGradient>
                </defs>

                <rect width="400" height="240" fill={`url(#${skyId})`} />
                <rect width="400" height="240" fill={`url(#${glowId})`} />

                <Cloud
                    cx={52}
                    cy={36}
                    className="lp-game__skyline-cloud lp-game__skyline-cloud--one"
                />
                <Cloud
                    cx={198}
                    cy={28}
                    scale={0.85}
                    className="lp-game__skyline-cloud lp-game__skyline-cloud--two"
                />
                <Cloud
                    cx={332}
                    cy={40}
                    scale={0.72}
                    className="lp-game__skyline-cloud lp-game__skyline-cloud--three"
                />

                <g fill="#c5e8dc" opacity="0.42">
                    {BUILDINGS_BACK.map((b) => (
                        <rect key={`back-${b.x}`} x={b.x} y={b.y} width={b.w} height={b.h} rx="1" />
                    ))}
                </g>

                <g>
                    {BUILDINGS_MID.map((b) => (
                        <Building key={`mid-${b.x}`} {...b} fill="#b3dfd0" opacity={0.62} />
                    ))}
                </g>

                <g>
                    {BUILDINGS_FRONT.map((b) => (
                        <Building key={`front-${b.x}`} {...b} fill="#9ed4c0" opacity={0.88} />
                    ))}
                </g>

                <path d={`M0 ${SKYLINE_GROUND_Y} H400 V240 H0 Z`} fill="#4bac90" />

                <Tree x={42} canopyY={152} scale={1.05} />
                <Tree x={146} canopyY={184} scale={1} />
                <Tree x={206} canopyY={164} scale={1} />
                <Tree x={246} canopyY={184} scale={1} />
                <Tree x={286} canopyY={154} scale={1} />
            </svg>
        </div>
    );
});
